/**
 * SPECTREIQ Architecture Visualization
 *
 * WHY: Displays the platform's modular architecture with animated data pulses
 * connecting the central hub to peripheral IQ modules. Uses pure CSS animations
 * for air-gap deployment compatibility (no framer-motion dependency).
 *
 * Positioning: Percentage-based for responsive scaling across viewports (1024px+).
 * Connector endpoints are measured from the live DOM (see useLayoutEffect) so the
 * pulses terminate on each module's name box, not the capabilities text below it.
 * Accessibility: Respects prefers-reduced-motion for users with vestibular disorders.
 */

import { useLayoutEffect, useRef, useState } from 'react'

import { useTheme } from '@/hooks/useTheme'

const IQ_MODULES = [
  {
    id: 'software',
    label: 'SoftwareIQ',
    capabilities: ['Software Assurance', 'Vulnerability Assessment', 'Control Assessment'],
    xPct: 15,
    yPct: 23,
    available: true,
  },
  {
    id: 'project',
    label: 'ProjectIQ',
    capabilities: ['Workflow Configuration', 'Project Tracking', 'Project Deconfliction'],
    xPct: 85,
    yPct: 23,
    available: true,
  },
  {
    id: 'financial',
    label: 'FinancialIQ',
    capabilities: ['Funding Analysis', 'Forecasting', 'Earned Value Management'],
    xPct: 92.5,
    yPct: 60,
    available: true,
  },
  {
    id: 'supply',
    label: 'SupplyIQ',
    capabilities: ['NDAA Compliance', 'Supply Chain Risk', 'Portfolio Risk'],
    xPct: 72.5,
    yPct: 83,
    available: true,
  },
  {
    id: 'metadata',
    label: 'MetadataIQ',
    capabilities: ['Mission Configurations', 'Security and Compliance', 'Module Integration'],
    xPct: 15,
    yPct: 73,
    available: true,
  },
]

const VIEW_W = 800
const VIEW_H = 600
const CENTER_X = VIEW_W / 2
const CENTER_Y = VIEW_H / 2

type Point = { x: number; y: number }

// The module anchor (xPct/yPct) is the centre of the name+capabilities group, so
// a line drawn to it lands in the capabilities text. This is the fallback used
// for the very first paint, before the title boxes are measured.
const anchorPoint = (m: (typeof IQ_MODULES)[number]): Point => ({
  x: (m.xPct / 100) * VIEW_W,
  y: (m.yPct / 100) * VIEW_H,
})

export function SpectreArchitecture() {
  const theme = useTheme()
  const isDark = theme === 'dark'

  const containerRef = useRef<HTMLDivElement>(null)
  const titleRefs = useRef<Array<HTMLDivElement | null>>([])
  const [ends, setEnds] = useState<Point[]>(() => IQ_MODULES.map(anchorPoint))

  // Terminate each connector at the name box's border facing the hub. We measure
  // the live title boxes (their pixel size is fixed by the font, while the SVG
  // viewBox scales with the container) and intersect the hub->box ray with the
  // box rectangle, so the pulse lands on the white outline at any viewport width.
  useLayoutEffect(() => {
    const compute = () => {
      const container = containerRef.current
      if (!container) return
      const cr = container.getBoundingClientRect()
      if (!cr.width || !cr.height) return
      const hub = { x: cr.width / 2, y: cr.height / 2 } // hub is centred at 50%/50%

      setEnds(
        IQ_MODULES.map((module, i) => {
          const el = titleRefs.current[i]
          if (!el) return anchorPoint(module)
          const r = el.getBoundingClientRect()
          const cx = (r.left + r.right) / 2 - cr.left
          const cy = (r.top + r.bottom) / 2 - cr.top
          const halfW = r.width / 2
          const halfH = r.height / 2

          const dx = cx - hub.x
          const dy = cy - hub.y

          // The capabilities list sits directly below the name box. For the top
          // row of modules the hub is below the box, so a ray to the box centre
          // would exit the bottom edge and skim across the capabilities text.
          // Terminate on the side edge facing the hub instead, so the connector
          // runs alongside the list rather than through it.
          if (hub.y > cy + halfH) {
            const sideX = cx + (dx < 0 ? halfW : -halfW)
            return { x: (sideX / cr.width) * VIEW_W, y: (cy / cr.height) * VIEW_H }
          }

          const len = Math.hypot(dx, dy) || 1
          const ux = dx / len
          const uy = dy / len

          // Distance from the box centre out to whichever edge the ray crosses.
          const toEdge = Math.min(halfW / (Math.abs(ux) || 1e-6), halfH / (Math.abs(uy) || 1e-6))
          const edgeX = cx - ux * toEdge
          const edgeY = cy - uy * toEdge

          return { x: (edgeX / cr.width) * VIEW_W, y: (edgeY / cr.height) * VIEW_H }
        }),
      )
    }

    compute()
    const observer = new ResizeObserver(compute)
    if (containerRef.current) observer.observe(containerRef.current)
    // Web-font load can change a title box's width without resizing the container.
    document.fonts?.ready.then(compute).catch(() => {})
    return () => observer.disconnect()
  }, [])

  return (
    <div ref={containerRef} className="relative w-full max-w-3xl aspect-[4/3]">
      {/* Central Hub */}
      <div
        className="absolute z-30 -translate-x-1/2 -translate-y-1/2"
        style={{ left: '50%', top: '50%' }}
      >
        <div
          className={`
            px-6 py-3 border-2 text-center
            ${isDark
              ? 'border-white/80 bg-black shadow-[0_0_40px_rgba(255,255,255,0.1)]'
              : 'border-gray-900 bg-white shadow-[0_0_40px_rgba(0,0,0,0.15)]'
            }
          `}
        >
          <span className={`text-[10px] tracking-[0.3em] block mb-1 ${isDark ? 'text-white/50' : 'text-gray-600'}`}>
            UNIFIED HUB
          </span>
          <h2 className={`text-xl font-bold tracking-tight italic ${isDark ? 'text-white' : 'text-black'}`}>
            SPECTREIQ PLATFORM
          </h2>
        </div>
      </div>

      {/* SVG Connection Lines with Animated Pulses */}
      <svg
        className="absolute inset-0 w-full h-full z-10 pointer-events-none"
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="xMidYMid meet"
      >
        {IQ_MODULES.map((module, index) => {
          const end = ends[index] ?? anchorPoint(module)

          return (
            <g key={`connection-${module.id}`}>
              <line
                x1={CENTER_X}
                y1={CENTER_Y}
                x2={end.x}
                y2={end.y}
                stroke={isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.4)'}
                strokeWidth="1"
              />
              <circle
                r="3"
                fill="#e26e32"
                className="animate-pulse-line"
                style={{
                  offsetPath: `path('M ${CENTER_X} ${CENTER_Y} L ${end.x} ${end.y}')`,
                  animationDelay: `${index * 0.8}s`,
                }}
              />
            </g>
          )
        })}
      </svg>

      {/* Module Nodes */}
      {IQ_MODULES.map((module, index) => (
        <div
          key={module.id}
          className="absolute flex flex-col items-start gap-2 z-20 -translate-x-1/2 -translate-y-1/2"
          style={{
            left: `${module.xPct}%`,
            top: `${module.yPct}%`,
          }}
        >
          <div
            ref={(el) => {
              titleRefs.current[index] = el
            }}
            className={`
              px-4 py-1.5 border backdrop-blur-sm uppercase text-sm font-bold italic tracking-widest
              ${isDark
                ? 'border-white/30 bg-black/80 text-white'
                : 'border-gray-900 bg-white/90 text-black'
              }
              ${!module.available ? (isDark ? 'opacity-50' : 'opacity-70') : ''}
            `}
          >
            {module.label}
            {!module.available && (
              <span className={`ml-2 text-[8px] normal-case not-italic tracking-normal ${isDark ? 'opacity-60' : 'opacity-80'}`}>
                (Soon)
              </span>
            )}
          </div>

          {/* Capabilities list - hidden below lg breakpoint */}
          <div
            className={`
              hidden lg:flex flex-col gap-1 text-[10px] tracking-tight uppercase pl-2 border-l border-[#e26e32]/40
              ${isDark ? 'text-white/70' : 'text-gray-800'}
              ${!module.available ? (isDark ? 'opacity-50' : 'opacity-70') : ''}
            `}
          >
            {module.capabilities.map((cap) => (
              <span key={cap} className="whitespace-nowrap">
                <span className="text-[#e26e32] mr-1">[</span>
                {cap}
                <span className="text-[#e26e32] ml-1">]</span>
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
